<div class="space-y-3">
    <!-- Toolbar -->
    <div class="flex items-center justify-between gap-3">
        <span class="text-xs font-medium text-gray-400">{{ tenants.length }} tenant{{ tenants.length !== 1 ? 's' : '' }}</span>
        <button (click)="showAddModal = true"
            class="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
            Add Tenant
        </button>
    </div>

    @if(loading) {
        <div class="flex justify-center items-center py-8">
            <div class="w-6 h-6 rounded-full border-3 border-blue-100 border-t-blue-600 animate-spin"></div>
        </div>
    } @else if(tenants.length === 0) {
        <div class="text-center py-8">
            <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
            </div>
            <p class="text-sm text-gray-500 font-medium">No tenants yet</p>
            <p class="text-xs text-gray-400 mt-1">Create your first tenant to get started</p>
        </div>
    } @else {
        @for(tenant of tenants; track tenant.id) {
            <div class="group relative bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-200 hover:shadow-sm transition-all"
                 [class.opacity-60]="!tenant.isActive">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                        <!-- Icon -->
                        <div class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                             [class.bg-blue-100]="tenant.isActive"
                             [class.bg-gray-100]="!tenant.isActive">
                            <svg class="w-5 h-5"
                                 [class.text-blue-600]="tenant.isActive"
                                 [class.text-gray-400]="!tenant.isActive"
                                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                        </div>

                        <!-- Name (editable) -->
                        @if(editingTenantId === tenant.id) {
                            <div class="flex items-center gap-2 flex-1">
                                <input type="text" [(ngModel)]="editingName"
                                       class="flex-1 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                       (keyup.enter)="saveEdit(tenant.id)"
                                       (keyup.escape)="cancelEdit()"
                                       autofocus/>
                                <button (click)="saveEdit(tenant.id)"
                                        class="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                                    </svg>
                                </button>
                                <button (click)="cancelEdit()"
                                        class="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        } @else {
                            <div class="flex-1 min-w-0">
                                <h3 class="font-semibold text-gray-900 truncate">{{ tenant.name }}</h3>
                                <div class="flex items-center gap-2 mt-0.5">
                                    <span class="text-xs px-2 py-0.5 rounded-full"
                                          [class.bg-green-100]="tenant.isActive"
                                          [class.text-green-700]="tenant.isActive"
                                          [class.bg-gray-100]="!tenant.isActive"
                                          [class.text-gray-500]="!tenant.isActive">
                                        {{ tenant.isActive ? 'Active' : 'Inactive' }}
                                    </span>
                                </div>
                            </div>
                        }
                    </div>

                    <!-- Actions -->
                    @if(editingTenantId !== tenant.id) {
                        <div class="flex items-center gap-2">
                            <!-- Toggle Active -->
                            <button (click)="toggleActive(tenant)"
                                    class="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                                    [title]="tenant.isActive ? 'Deactivate' : 'Activate'">
                                @if(tenant.isActive) {
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                } @else {
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                }
                            </button>

                            <!-- Menu Button -->
                            <div class="relative">
                                <button (click)="toggleMenu(tenant.id, $event)"
                                        class="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01"/>
                                    </svg>
                                </button>

                                @if(openMenuId === tenant.id) {
                                    <div class="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden"
                                         (click)="$event.stopPropagation()">
                                        <button (click)="startEdit(tenant, $event)"
                                                class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                            </svg>
                                            Rename
                                        </button>
                                        <button (click)="confirmDelete(tenant.id, $event)"
                                                class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                            </svg>
                                            Delete
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
        }
    }
</div>

@if(showConfirmDelete) {
    <app-confirm-modal
        [message]="'Are you sure you want to delete this tenant? All associated data will be affected.'"
        (close)="handleConfirmDelete($event)">
    </app-confirm-modal>
}

@if(showAddModal) {
    <div class="fixed inset-0 z-9999 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
         (click)="closeAddModal()">
        <div (click)="$event.stopPropagation()" class="w-full max-w-lg">
            <app-add-tenant (close)="closeAddModal()" />
        </div>
    </div>
}
